// Las plantillas de abajo de la caja del modo Redes — ADR-2026-10-01-modo-redes.
//
// La MISMA anatomía que la página de Plantillas (`InterfazV2Plantillas`): una fila de
// chips de categoría —tocar uno filtra, tocar el activo lo apaga— y la grilla masonry
// con sus columnas y su ciclo de aspectos. Cambia con el medio:
//
//  • Imagen: las colecciones del catálogo; tocar una abre el flujo de plantilla de siempre.
//  • Video: los tipos de video del Creador, con las MISMAS cards (el clip 9:16 en loop) y
//    sus categorías como chips; elegir uno lo ancla en la caja.
//
// En los dos casos la PRIMERA card es "Copiar imagen" / "Copiar video": se anuncia y por
// ahora no hace nada.

import React, { useEffect, useMemo, useState } from 'react';
import { Copy } from 'lucide-react';
import { useAgentEnv } from '../../agentEnvContext';
import { useRubroMarcaActiva } from '../../../../hooks/useRubroMarca';
import { rubroVePlantillas } from '../../../../lib/rubro';
import type { CollectionV2, PresetV2 } from '../../mockData';
import { fetchTemplatesV2 } from '../../templatesApi';
import { ASPECT_CYCLE, MasonryCard } from '../../components/TemplateMasonry';
import { agruparPorCategoria, useVideoTipos, type TipoDeVideo } from '../../videoTiposCatalog';
import type { Medio } from './redes';
import { ColumnasParallax } from './ColumnasParallax';
import { claseDeChip } from '../../components/chipsDeCategoria';

export interface PlantillaElegida {
  preset: PresetV2;
  collectionId: string;
  collectionPresets: PresetV2[];
}

/** Las columnas y el gap de la grilla de Plantillas. */
const MASONRY = 'columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4';

let cacheDeColecciones: { clave: string; colecciones: CollectionV2[] } | null = null;

function useColeccionesDeRedes(): CollectionV2[] | null {
  const { companyId } = useAgentEnv();
  const { rubro, loaded } = useRubroMarcaActiva();
  const clave = `${companyId ?? ''}:${rubro ?? ''}`;
  const [colecciones, setColecciones] = useState<CollectionV2[] | null>(() =>
    cacheDeColecciones?.clave === clave ? cacheDeColecciones.colecciones : null,
  );
  useEffect(() => {
    if (!loaded) return;
    if (!rubroVePlantillas(rubro)) {
      setColecciones([]);
      return;
    }
    if (cacheDeColecciones?.clave === clave) {
      setColecciones(cacheDeColecciones.colecciones);
      return;
    }
    let vivo = true;
    void fetchTemplatesV2({ rubro, companyId }).then((c) => {
      cacheDeColecciones = { clave, colecciones: c };
      if (vivo) setColecciones(c);
    });
    return () => {
      vivo = false;
    };
  }, [loaded, rubro, companyId, clave]);
  return colecciones;
}

/**
 * La fila de chips. `activo === null` = sin filtro. Tocar el activo lo apaga.
 *
 * Queda PEGADA arriba al scrollear, como en la página de Plantillas. Allá hace falta un
 * sentinel + `position: fixed` porque el panel del shell no es el que scrollea
 * (ADR-171); acá el scroller es el de esta pantalla, así que `sticky` anda solo. El fondo
 * en degradé es el mismo de allá: sólido arriba, se funde hacia la grilla.
 */
const Chips: React.FC<{
  categorias: { id: string; nombre: string }[];
  activo: string | null;
  onCambiar: (id: string | null) => void;
}> = ({ categorias, activo, onCambiar }) => (
  <div
    className="sticky top-0 z-20 -mx-6 flex gap-2 overflow-x-auto px-6 py-3 scrollbar-hide sm:py-4"
    style={{
      background:
        'linear-gradient(to bottom, var(--plantillas-sticky-bg, #F9F9F9) 0%, var(--plantillas-sticky-bg, #F9F9F9) 70%, var(--plantillas-sticky-bg-0, rgba(249,249,249,0)) 100%)',
    }}
  >
    {categorias.map((c) => {
      const esActivo = c.id === activo;
      return (
        <button
          key={c.id}
          type="button"
          aria-pressed={esActivo}
          onClick={() => onCambiar(esActivo ? null : c.id)}
          className={claseDeChip(esActivo)}
        >
          {c.nombre}
        </button>
      );
    })}
  </div>
);

/** La card de "Copiar": misma anatomía que las de plantilla, sin acción todavía. */
const CardCopiar: React.FC<{ rotulo: string; aspecto: string; className?: string }> = ({ rotulo, aspecto, className = '' }) => (
  <div aria-disabled="true" className={`relative mb-3 w-full break-inside-avoid overflow-hidden rounded-xl bg-[#EEF0F5] sm:mb-4 ${aspecto} ${className}`}>
    <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#02062B99]">
      <Copy className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
      <span className="text-[13px] font-semibold">{rotulo}</span>
      <span className="rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-bold text-brand-black shadow-sm">Próximamente</span>
    </span>
  </div>
);

/** Un tipo de video como una card de plantilla: el clip en loop, el nombre abajo. */
const presetDeTipo = (t: TipoDeVideo): PresetV2 => ({
  id: t.slug,
  title: t.nombre,
  imageUrl: t.posterUrl ?? '',
  imageUrlFull: t.videoUrl ?? '',
  outputType: 'video',
  kind: 'template',
});

const Esqueleto: React.FC = () => (
  <div className={MASONRY} aria-hidden="true">
    {[4 / 5, 1, 3 / 4, 4 / 5, 1, 3 / 4, 4 / 5, 1].map((aspecto, i) => (
      <div key={i} className="mb-3 break-inside-avoid rounded-xl bg-black/[0.06] animate-pulse sm:mb-4" style={{ aspectRatio: String(aspecto) }} />
    ))}
  </div>
);

export const PlantillasDeRedes: React.FC<{
  medio: Medio;
  tipoVideo: string;
  onTipoVideo: (slug: string) => void;
  onElegirPlantilla: (p: PlantillaElegida) => void;
}> = ({ medio, tipoVideo, onTipoVideo, onElegirPlantilla }) => {
  const colecciones = useColeccionesDeRedes();
  const { tipos, cargando: cargandoTipos } = useVideoTipos();
  const [categoriaImagen, setCategoriaImagen] = useState<string | null>(null);
  const [categoriaVideo, setCategoriaVideo] = useState<string | null>(null);

  const planas = useMemo(
    () =>
      (colecciones ?? []).flatMap((c) =>
        c.presets.map((p) => ({ preset: p, collectionId: c.id, collectionPresets: c.presets })),
      ),
    [colecciones],
  );
  const grupos = useMemo(() => agruparPorCategoria(tipos), [tipos]);

  if (medio === 'video') {
    const visibles = grupos.filter((g) => categoriaVideo === null || g.categoria === categoriaVideo).flatMap((g) => g.tipos);
    return (
      <section id="plantillas-de-redes" className="mt-8 scroll-mt-4">
        <h2 className="text-[34px] font-bold leading-[1.15] tracking-[-0.02em] text-brand-black">Explora las plantillas</h2>
        <Chips
          categorias={grupos.map((g) => ({ id: g.categoria, nombre: g.categoria }))}
          activo={categoriaVideo}
          onCambiar={setCategoriaVideo}
        />
        {cargandoTipos && tipos.length === 0 ? (
          <Esqueleto />
        ) : (
          // Columnas explícitas con parallax (las cards se reparten de a una por columna,
          // así no queda una vacía con pocos ítems). Mismo gap que la masonry.
          <ColumnasParallax
            items={[
              <CardCopiar key="copiar" rotulo="Copiar video" aspecto="aspect-[9/16]" />,
              ...visibles.map((t) => {
                const elegido = t.slug === tipoVideo;
                return (
                  <div key={t.slug} className={`mb-3 rounded-xl sm:mb-4 ${elegido ? 'ring-2 ring-brand-primary ring-offset-2' : ''}`}>
                    <MasonryCard preset={presetDeTipo(t)} aspectClass="aspect-[9/16] !mb-0" onClick={() => onTipoVideo(elegido ? 'general' : t.slug)} />
                  </div>
                );
              }),
            ]}
          />
        )}
      </section>
    );
  }

  const visibles = categoriaImagen === null ? planas : planas.filter((p) => p.collectionId === categoriaImagen);
  return (
    <section id="plantillas-de-redes" className="mt-8 scroll-mt-4">
      <h2 className="text-[34px] font-bold leading-[1.15] tracking-[-0.02em] text-brand-black">Explora las plantillas</h2>
      <Chips
        categorias={(colecciones ?? []).map((c) => ({ id: c.id, nombre: c.title }))}
        activo={categoriaImagen}
        onCambiar={setCategoriaImagen}
      />
      {colecciones === null ? (
        <Esqueleto />
      ) : (
        // La misma masonry (ciclo de aspectos) en columnas explícitas con parallax.
        <ColumnasParallax
          items={[
            <CardCopiar key="copiar" rotulo="Copiar imagen" aspecto="aspect-[3/4]" />,
            ...visibles.map((item, i) => (
              <MasonryCard
                key={`${item.collectionId}:${item.preset.id}`}
                preset={item.preset}
                aspectClass={ASPECT_CYCLE[(i + 1) % ASPECT_CYCLE.length]}
                onClick={() => onElegirPlantilla(item)}
              />
            )),
          ]}
        />
      )}
    </section>
  );
};

export default PlantillasDeRedes;
